/**
 * Color utility functions for button styling
 */

/**
 * Validates if a string is a valid CSS color
 * Supports: hex (#fff, #ffffff), rgb(a), hsl(a), and named colors
 */
export function isValidCSSColor(color: string): boolean {
  if (!color || typeof color !== 'string') {
    return false;
  }

  // Trim whitespace
  const trimmed = color.trim();

  // Check hex colors (#fff or #ffffff)
  if (/^#([0-9A-F]{3}){1,2}$/i.test(trimmed)) {
    return true;
  }

  // Check rgb/rgba
  if (/^rgba?\(\s*\d+\s*,\s*\d+\s*,\s*\d+\s*(,\s*[\d.]+\s*)?\)$/i.test(trimmed)) {
    return true;
  }

  // Check hsl/hsla
  if (/^hsla?\(\s*\d+\s*,\s*\d+%\s*,\s*\d+%\s*(,\s*[\d.]+\s*)?\)$/i.test(trimmed)) {
    return true;
  }

  // Check named colors (create temporary element to test)
  const s = new Option().style;
  s.color = trimmed;
  return s.color !== '';
}

/**
 * Darkens a color by a specified amount
 * @param color - CSS color string (hex, rgb, or named)
 * @param amount - Amount to darken (0-1, where 0.1 = 10% darker)
 * @returns Darkened color in hex format
 */
export function darkenColor(color: string, amount: number): string {
  if (!isValidCSSColor(color)) {
    console.warn('[ColorUtils] Invalid color provided to darkenColor:', color);
    return color;
  }

  // Convert to RGB
  const rgb = colorToRGB(color);
  if (!rgb) {
    return color;
  }

  // Darken by reducing RGB values
  const factor = 1 - Math.max(0, Math.min(1, amount));
  const r = Math.round(rgb.r * factor);
  const g = Math.round(rgb.g * factor);
  const b = Math.round(rgb.b * factor);

  // Convert back to hex
  return `#${((1 << 24) + (r << 16) + (g << 8) + b).toString(16).slice(1)}`;
}

/**
 * Converts any CSS color to RGB object.
 * Handles hex (#rgb / #rrggbb), rgb/rgba, and hsl/hsla without touching the DOM.
 * Named colors (e.g. "red") are not resolved — callers fall back to the original color.
 * @param color - CSS color string
 * @returns RGB object or null if the format is unrecognised
 */
function colorToRGB(color: string): { r: number; g: number; b: number } | null {
  const trimmed = color.trim();

  // Hex: #rgb or #rrggbb
  const hexMatch = trimmed.match(/^#([0-9A-F]{6}|[0-9A-F]{3})$/i);
  if (hexMatch && hexMatch[1]) {
    const hex = hexMatch[1];
    if (hex.length === 3) {
      return {
        r: parseInt(hex.charAt(0).repeat(2), 16),
        g: parseInt(hex.charAt(1).repeat(2), 16),
        b: parseInt(hex.charAt(2).repeat(2), 16),
      };
    }
    return {
      r: parseInt(hex.slice(0, 2), 16),
      g: parseInt(hex.slice(2, 4), 16),
      b: parseInt(hex.slice(4, 6), 16),
    };
  }

  // rgb(r, g, b) or rgba(r, g, b, a)
  const rgbMatch = trimmed.match(/^rgba?\(\s*(\d+)\s*,\s*(\d+)\s*,\s*(\d+)/i);
  if (rgbMatch && rgbMatch[1] && rgbMatch[2] && rgbMatch[3]) {
    return {
      r: parseInt(rgbMatch[1], 10),
      g: parseInt(rgbMatch[2], 10),
      b: parseInt(rgbMatch[3], 10),
    };
  }

  // hsl(h, s%, l%) or hsla(h, s%, l%, a)
  const hslMatch = trimmed.match(/^hsla?\(\s*(\d+)\s*,\s*(\d+)%\s*,\s*(\d+)%/i);
  if (hslMatch && hslMatch[1] && hslMatch[2] && hslMatch[3]) {
    return hslToRGB(parseInt(hslMatch[1], 10), parseInt(hslMatch[2], 10), parseInt(hslMatch[3], 10));
  }

  return null;
}

/** Converts HSL values to an RGB object (all channels 0–255). */
function hslToRGB(h: number, s: number, l: number): { r: number; g: number; b: number } {
  const sn = s / 100;
  const ln = l / 100;
  const k = (n: number) => (n + h / 30) % 12;
  const a = sn * Math.min(ln, 1 - ln);
  const channel = (n: number) => Math.round((ln - a * Math.max(-1, Math.min(k(n) - 3, Math.min(9 - k(n), 1)))) * 255);
  return { r: channel(0), g: channel(8), b: channel(4) };
}

/**
 * Lightens a color by a specified amount
 * @param color - CSS color string (hex, rgb, or named)
 * @param amount - Amount to lighten (0-1, where 0.1 = 10% lighter)
 * @returns Lightened color in hex format
 */
export function lightenColor(color: string, amount: number): string {
  if (!isValidCSSColor(color)) {
    console.warn('[ColorUtils] Invalid color provided to lightenColor:', color);
    return color;
  }

  const rgb = colorToRGB(color);
  if (!rgb) {
    return color;
  }

  // Lighten by moving towards white
  const factor = Math.max(0, Math.min(1, amount));
  const r = Math.round(rgb.r + (255 - rgb.r) * factor);
  const g = Math.round(rgb.g + (255 - rgb.g) * factor);
  const b = Math.round(rgb.b + (255 - rgb.b) * factor);

  return `#${((1 << 24) + (r << 16) + (g << 8) + b).toString(16).slice(1)}`;
}

/**
 * Default submit button background color
 */
export const DEFAULT_SUBMIT_BUTTON_COLOR = '#ffffff';

/**
 * Default submit button foreground (text) color
 */
export const DEFAULT_SUBMIT_BUTTON_FOREGROUND_COLOR = '#242424';


/**
 * Default submit button label
 */
export const DEFAULT_SUBMIT_BUTTON_LABEL = 'Submit Details';
